import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import ChordDiagram from '../components/ChordDiagram';
import PremiumLock from '../components/PremiumLock';
import { Music, Filter, Crown, Search, ChevronLeft, ChevronRight, Heart, Play, ArrowDown, ArrowUp, Repeat, Volume2, Lock, Guitar, Sparkles, BookOpen, X } from 'lucide-react';
import { useSoundfont, fretToMidi } from '../hooks/useSoundfont';

const CATEGORY_THEMES = {
  major:   { gradient: 'from-amber to-orange-500',     bg: 'bg-amber/10',         border: 'border-amber/30',         text: 'text-amber' },
  minor:   { gradient: 'from-blue to-cyan-400',        bg: 'bg-cyan-500/10',      border: 'border-cyan-500/30',      text: 'text-cyan-300' },
  '7th':   { gradient: 'from-purple-500 to-pink-500',  bg: 'bg-purple-500/10',    border: 'border-purple-500/30',    text: 'text-purple-300' },
  maj7:    { gradient: 'from-green-500 to-emerald-400',bg: 'bg-emerald-500/10',   border: 'border-emerald-500/30',   text: 'text-emerald-300' },
  m7:      { gradient: 'from-teal-500 to-cyan-500',    bg: 'bg-teal-500/10',      border: 'border-teal-500/30',      text: 'text-teal-300' },
  sus:     { gradient: 'from-pink-500 to-rose-400',    bg: 'bg-pink-500/10',      border: 'border-pink-500/30',      text: 'text-pink-300' },
  dim:     { gradient: 'from-red-500 to-rose-500',     bg: 'bg-red-500/10',       border: 'border-red-500/30',       text: 'text-red-300' },
  aug:     { gradient: 'from-orange-500 to-yellow-500',bg: 'bg-orange-500/10',    border: 'border-orange-500/30',    text: 'text-orange-300' },
  add:     { gradient: 'from-indigo-500 to-blue-500',  bg: 'bg-indigo-500/10',    border: 'border-indigo-500/30',    text: 'text-indigo-300' },
  power:   { gradient: 'from-red-600 to-orange-600',   bg: 'bg-red-500/10',       border: 'border-red-500/30',       text: 'text-red-300' },
};

const getCategoryTheme = (cat) => CATEGORY_THEMES[cat] || CATEGORY_THEMES.major;

const getTheoryNote = (cat, t) => t(`chords.theoryNotes.${cat}`, { defaultValue: '' }) || null;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============= CHORD AUDIO ENGINE =============
// Note frequencies (A4 = 440Hz standard)
const NOTE_FREQUENCIES = {
  'C': 261.63, 'C#': 277.18, 'Db': 277.18,
  'D': 293.66, 'D#': 311.13, 'Eb': 311.13,
  'E': 329.63,
  'F': 349.23, 'F#': 369.99, 'Gb': 369.99,
  'G': 392.00, 'G#': 415.30, 'Ab': 415.30,
  'A': 440.00, 'A#': 466.16, 'Bb': 466.16,
  'B': 493.88
};

// Guitar standard tuning (E2 A2 D3 G3 B3 E4) - frequencies
const GUITAR_OPEN_STRINGS = [
  82.41,   // E2 (string 6 - lowest)
  110.00,  // A2 (string 5)
  146.83,  // D3 (string 4)
  196.00,  // G3 (string 3)
  246.94,  // B3 (string 2)
  329.63   // E4 (string 1 - highest)
];

// Calculate frequency for a fret position on a string
const getFretFrequency = (stringIndex, fret) => {
  if (fret < 0) return null; // X = muted string
  const openFreq = GUITAR_OPEN_STRINGS[stringIndex];
  // Each fret is a semitone = multiply by 2^(1/12)
  return openFreq * Math.pow(2, fret / 12);
};

// Audio engine constants
const STRUM_DELAY = 20; // ms between strings
const ARPEGGIO_DELAY = 150; // ms between notes in arpeggio
const NOTE_DURATION = 1.0;
const MAX_GAIN = 0.18;

// Filter settings for warmer tone
const FILTER_FREQ = 2000; // Hz - low-pass cutoff
const FILTER_Q = 0.9; // Resonance

// Detune for chorus effect (cents)
const DETUNE_AMOUNT = 4;

const ChordsPage = () => {
  const { t, i18n } = useTranslation();
  const { isPremium, isAuthenticated } = useAuth();
  const [chords, setChords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState({ categories: [], root_notes: [], chord_types: [] });
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedRoot, setSelectedRoot] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChord, setSelectedChord] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0 });
  
  // Audio state
  const [chordVolume, setChordVolume] = useState(70);
  const [isArpeggioPlaying, setIsArpeggioPlaying] = useState(false);
  const isArpeggioPlayingRef = useRef(false);
  const audioCtxRef = useRef(null);
  const masterGainRef = useRef(null);
  const arpeggioTimeoutRef = useRef([]);
  const [audioCtx, setAudioCtx] = useState(null);
  const [useRealisticGuitar, setUseRealisticGuitar] = useState(true);
  const { instrument, ready: instrumentReady } = useSoundfont(
    'acoustic_guitar_nylon',
    audioCtx,
    { enabled: useRealisticGuitar }
  );

  // Sync ref with state
  useEffect(() => {
    isArpeggioPlayingRef.current = isArpeggioPlaying;
  }, [isArpeggioPlaying]);

  // Initialize AudioContext on first interaction
  const getAudioContext = useCallback(() => {
    if (!audioCtxRef.current) {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      masterGainRef.current = ctx.createGain();
      masterGainRef.current.connect(ctx.destination);
      masterGainRef.current.gain.value = chordVolume / 100;
      audioCtxRef.current = ctx;
      setAudioCtx(ctx);
    }
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  }, [chordVolume]);

  // Update master gain when volume changes
  useEffect(() => {
    if (masterGainRef.current) {
      masterGainRef.current.gain.value = chordVolume / 100;
    }
  }, [chordVolume]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isArpeggioPlayingRef.current = false;
      arpeggioTimeoutRef.current.forEach(t => clearTimeout(t));
      arpeggioTimeoutRef.current = [];
      if (audioCtxRef.current) {
        try { audioCtxRef.current.close(); } catch (_) {}
        audioCtxRef.current = null;
        masterGainRef.current = null;
      }
    };
  }, []);

  // Play a single note with improved guitar-like tone
  const playNote = useCallback((frequency, startTime, duration = NOTE_DURATION, midi = null) => {
    const ctx = getAudioContext();
    if (!ctx) return;

    if (useRealisticGuitar && instrument && midi != null) {
      try {
        instrument.play(midi, startTime, {
          destination: masterGainRef.current,
          duration: Math.max(duration, 1.5),
          gain: 1.0,
        });
      } catch (_) { /* fall through to synth */ }
      return;
    }
    if (!frequency) return;

    // Create gain node with guitar-like envelope
    const gainNode = ctx.createGain();
    
    // Create low-pass filter for warmer tone
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(FILTER_FREQ, startTime);
    filter.Q.setValueAtTime(FILTER_Q, startTime);

    // Create 2 detuned oscillators for richer sound
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    
    osc1.type = 'sawtooth';
    osc2.type = 'triangle';
    
    osc1.frequency.setValueAtTime(frequency, startTime);
    osc2.frequency.setValueAtTime(frequency, startTime);
    
    osc1.detune.setValueAtTime(-DETUNE_AMOUNT, startTime);
    osc2.detune.setValueAtTime(+DETUNE_AMOUNT, startTime);

    // Guitar-like envelope
    const t0 = startTime;
    const peak = MAX_GAIN;
    
    gainNode.gain.setValueAtTime(0.0001, t0);
    gainNode.gain.exponentialRampToValueAtTime(peak, t0 + 0.008);
    gainNode.gain.exponentialRampToValueAtTime(peak * 0.3, t0 + 0.1);
    gainNode.gain.exponentialRampToValueAtTime(peak * 0.15, t0 + 0.35);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.9);

    osc1.connect(filter);
    osc2.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(masterGainRef.current);

    osc1.start(startTime);
    osc2.start(startTime);
    osc1.stop(startTime + duration);
    osc2.stop(startTime + duration);
  }, [getAudioContext, instrument, useRealisticGuitar]);

  // Get frequencies for a chord
  const getChordFrequencies = useCallback((positions) => {
    if (!positions || positions.length !== 6) return [];
    return positions.map((fret, idx) => getFretFrequency(idx, fret)).filter(f => f !== null);
  }, []);

  // Play chord - all notes simultaneously
  const playChordSimultaneous = useCallback((positions) => {
    const ctx = getAudioContext();
    if (!ctx) return;

    const startTime = ctx.currentTime + 0.05;
    positions.forEach((fret, stringIdx) => {
      const freq = getFretFrequency(stringIdx, fret);
      const midi = fretToMidi(stringIdx, fret);
      if (freq) {
        playNote(freq, startTime, NOTE_DURATION, midi);
      }
    });
  }, [getAudioContext, playNote]);

  // Strum chord - notes with delay
  const playChordStrum = useCallback((positions, direction = 'down') => {
    const ctx = getAudioContext();
    if (!ctx) return;

    const startTime = ctx.currentTime + 0.05;
    const strings = direction === 'down'
      ? [0, 1, 2, 3, 4, 5] // Low to high
      : [5, 4, 3, 2, 1, 0]; // High to low

    strings.forEach((stringIdx, i) => {
      const fret = positions[stringIdx];
      const freq = getFretFrequency(stringIdx, fret);
      const midi = fretToMidi(stringIdx, fret);
      if (freq) {
        const noteTime = startTime + (i * STRUM_DELAY / 1000);
        playNote(freq, noteTime, NOTE_DURATION, midi);
      }
    });
  }, [getAudioContext, playNote]);

  // Arpeggio - notes one at a time in loop (PRO only)
  const playArpeggio = useCallback((positions) => {
    if (!isPremium) return;
    
    // Stop any existing arpeggio
    arpeggioTimeoutRef.current.forEach(t => clearTimeout(t));
    arpeggioTimeoutRef.current = [];
    
    // Set ref IMMEDIATELY (state update is async)
    isArpeggioPlayingRef.current = true;
    setIsArpeggioPlaying(true);

    const ctx = getAudioContext();
    if (!ctx) return;

    // Get valid notes (non-muted strings)
    const notes = [];
    positions.forEach((fret, stringIdx) => {
      const freq = getFretFrequency(stringIdx, fret);
      const midi = fretToMidi(stringIdx, fret);
      if (freq) {
        notes.push({ freq, stringIdx, midi });
      }
    });

    if (notes.length === 0) {
      isArpeggioPlayingRef.current = false;
      setIsArpeggioPlaying(false);
      return;
    }

    // Play notes in sequence using ref for state check
    let noteIndex = 0;
    const playNextNote = () => {
      if (!isArpeggioPlayingRef.current) return;

      const ctx = getAudioContext();
      if (!ctx) return;

      const { freq, midi } = notes[noteIndex];
      playNote(freq, ctx.currentTime + 0.01, 0.3, midi);

      noteIndex = (noteIndex + 1) % notes.length;

      // Only the pending timeout matters; replace instead of pushing so the
      // ref does not grow unbounded over a long-running arpeggio loop.
      const timeoutId = setTimeout(playNextNote, ARPEGGIO_DELAY);
      arpeggioTimeoutRef.current = [timeoutId];
    };

    playNextNote();
  }, [isPremium, getAudioContext, playNote]);

  // Stop arpeggio
  const stopArpeggio = useCallback(() => {
    arpeggioTimeoutRef.current.forEach(t => clearTimeout(t));
    arpeggioTimeoutRef.current = [];
    isArpeggioPlayingRef.current = false;
    setIsArpeggioPlaying(false);
  }, []);

  useEffect(() => {
    fetchCategories();
    fetchChords();
    if (isAuthenticated) fetchFavorites();
  }, []);

  useEffect(() => {
    fetchChords();
  }, [selectedDifficulty, selectedCategory, selectedRoot, searchQuery, pagination.offset]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/chords/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchChords = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedDifficulty !== 'all') params.append('difficulty', selectedDifficulty);
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (selectedRoot !== 'all') params.append('root_note', selectedRoot);
      if (searchQuery) params.append('search', searchQuery);
      params.append('limit', pagination.limit);
      params.append('offset', pagination.offset);

      const response = await axios.get(`${API}/chords?${params.toString()}`);
      setChords(response.data.items);
      setPagination(prev => ({ ...prev, total: response.data.total }));
    } catch (error) {
      console.error('Error fetching chords:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites?item_type=chord`);
      setFavorites(response.data.map(f => f.item_id));
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  const toggleFavorite = async (chordId) => {
    if (!isAuthenticated) return;
    try {
      if (favorites.includes(chordId)) {
        await axios.delete(`${API}/favorites/chord/${chordId}`);
        setFavorites(prev => prev.filter(id => id !== chordId));
      } else {
        await axios.post(`${API}/favorites?item_type=chord&item_id=${chordId}`);
        setFavorites(prev => [...prev, chordId]);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const getChordName = (chord) => {
    return i18n.language === 'it' ? chord.full_name_it : chord.full_name;
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;

  const goToPage = (page) => {
    setPagination(prev => ({ ...prev, offset: (page - 1) * prev.limit }));
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20 pb-12 px-4 md:px-8 lg:px-12" data-testid="chords-page">
      <div className="max-w-7xl mx-auto">
        {/* Magazine header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <span className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full bg-amber/10 border border-amber/30 text-amber text-xs font-bold uppercase tracking-widest">
            <Music className="w-3 h-3" />
            {t('chords.tagline', { defaultValue: 'Libreria accordi' })}
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-3">
            {t('chords.title')}
          </h1>
          <p className="text-zinc-400 max-w-2xl">
            {t('chords.subtitle', {
              defaultValue: 'Diagrammi, dita, varianti e barré per 209 accordi. Click su un accordo per sentirlo con campioni di chitarra acustica reale.',
            })}
          </p>
        </motion.div>

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-[#121212] border border-white/5 rounded-2xl p-5 mb-8 space-y-4"
        >
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('chords.searchPlaceholder')}
              data-testid="chord-search"
              className="pl-10 bg-[#0A0A0A] border-white/10 text-white"
            />
          </div>

          {/* Difficulty pills */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mr-1">
              {t('chords.difficulty')}
            </span>
            {[
              { v: 'all', label: t('common.all') },
              { v: 'beginner', label: t('levels.beginner') },
              { v: 'intermediate', label: t('levels.intermediate') },
              { v: 'advanced', label: t('levels.expert') },
            ].map(opt => (
              <button
                key={opt.v}
                onClick={() => setSelectedDifficulty(opt.v)}
                data-testid={`difficulty-${opt.v}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  selectedDifficulty === opt.v
                    ? 'bg-amber text-black'
                    : 'bg-white/[0.03] border border-white/10 text-zinc-400 hover:text-white'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Root note pills */}
          {categories.root_notes?.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mr-1">
                {t('chords.rootNote')}
              </span>
              <button
                onClick={() => setSelectedRoot('all')}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  selectedRoot === 'all'
                    ? 'bg-white text-black'
                    : 'bg-white/[0.03] border border-white/10 text-zinc-400 hover:text-white'
                }`}
              >
                {t('common.all')}
              </button>
              {categories.root_notes.map(note => (
                <button
                  key={note}
                  onClick={() => setSelectedRoot(note)}
                  className={`px-3 py-1 rounded-full text-xs font-mono font-bold transition-colors min-w-[40px] ${
                    selectedRoot === note
                      ? 'bg-amber text-black'
                      : 'bg-white/[0.03] border border-white/10 text-zinc-300 hover:text-white'
                  }`}
                >
                  {note}
                </button>
              ))}
            </div>
          )}

          {/* Category pills */}
          {categories.categories?.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mr-1">
                {t('chords.category')}
              </span>
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  selectedCategory === 'all'
                    ? 'bg-white text-black'
                    : 'bg-white/[0.03] border border-white/10 text-zinc-400 hover:text-white'
                }`}
              >
                {t('common.all')}
              </button>
              {categories.categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                    selectedCategory === cat
                      ? 'bg-amber text-black'
                      : 'bg-white/[0.03] border border-white/10 text-zinc-400 hover:text-white'
                  }`}
                >
                  {t(`chords.categories.${cat}`) || cat}
                </button>
              ))}
            </div>
          )}

          {/* Audio controls */}
          <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-white/5">
            <div className="flex items-center gap-2 bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-1.5">
              <Volume2 className="w-4 h-4 text-amber" />
              <Slider
                value={[chordVolume]}
                onValueChange={(v) => setChordVolume(v[0])}
                min={0}
                max={100}
                step={5}
                className="w-24"
                data-testid="chord-volume-slider"
              />
              <span className="text-zinc-400 text-xs w-8">{chordVolume}%</span>
            </div>

            <button
              onClick={() => setUseRealisticGuitar(v => !v)}
              title={t('chords.realisticToggleTitle', { defaultValue: 'Chitarra acustica reale vs synth' })}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs font-bold uppercase tracking-wider transition-colors ${
                useRealisticGuitar
                  ? 'bg-amber/10 border-amber/40 text-amber'
                  : 'bg-[#0A0A0A] border-white/10 text-zinc-400 hover:text-white'
              }`}
              data-testid="chord-realistic-toggle"
            >
              {useRealisticGuitar ? <Guitar className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
              {useRealisticGuitar
                ? (instrumentReady
                    ? t('chords.realisticOn', { defaultValue: 'Reale' })
                    : t('chords.realisticLoading', { defaultValue: 'Carico...' }))
                : t('chords.realisticOff', { defaultValue: 'Synth' })}
            </button>
          </div>
        </motion.div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-amber border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Chords Grid */}
        {!loading && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {chords.map((chord, index) => (
                <motion.div
                  key={chord.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index * 0.02, 0.5) }}
                  onClick={() => !chord.locked && setSelectedChord(chord)}
                  className={`relative bg-[#121212] border border-white/5 rounded-xl p-3 hover:border-amber/30 transition-all cursor-pointer group ${chord.locked ? 'opacity-60' : ''}`}
                  data-testid={`chord-card-${chord.id}`}
                >
                  {chord.locked && <PremiumLock />}
                  
                  <div className="absolute top-2 right-2 z-20 flex gap-1">
                    {chord.is_premium && (
                      <Crown className="w-4 h-4 text-amber" />
                    )}
                    {isAuthenticated && !chord.locked && (
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleFavorite(chord.id); }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Heart 
                          className={`w-4 h-4 ${favorites.includes(chord.id) ? 'fill-red-500 text-red-500' : 'text-zinc-400'}`} 
                        />
                      </button>
                    )}
                  </div>

                  <div className="flex flex-col items-center">
                    <ChordDiagram chord={{...chord, name: chord.name}} size="small" />
                  </div>

                  <div className="mt-2 text-center">
                    <p className="text-xs text-zinc-400 truncate">{getChordName(chord)}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      chord.difficulty === 'beginner' ? 'bg-green-500/20 text-green-400' :
                      chord.difficulty === 'intermediate' ? 'bg-blue/20 text-blue' :
                      'bg-purple-500/20 text-purple-400'
                    }`}>
                      {t(`levels.${chord.difficulty === 'advanced' ? 'expert' : chord.difficulty}`)}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="text-zinc-400"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                
                <span className="text-zinc-400 text-sm">
                  {t('chords.pageInfo', { page: currentPage, total: totalPages })}
                </span>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="text-zinc-400"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        )}

        {/* Selected Chord Modal — magazine treatment */}
        {selectedChord && (() => {
          const theme = getCategoryTheme(selectedChord.category);
          const theoryNote = getTheoryNote(selectedChord.category, t);
          return (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto"
              onClick={() => { stopArpeggio(); setSelectedChord(null); }}
            >
              <div className="min-h-full flex items-center justify-center p-4">
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="bg-[#0E0E10] border border-white/10 rounded-3xl max-w-2xl w-full overflow-hidden my-8"
                  onClick={e => e.stopPropagation()}
                >
                  {/* Colored header banner */}
                  <div className={`relative bg-gradient-to-br ${theme.gradient} px-6 sm:px-8 py-7`}>
                    <div className="absolute inset-0 bg-black/30" />
                    <div className="relative z-10 flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <span className="text-[10px] uppercase tracking-widest text-white/70 font-bold">
                          {t(`chords.categories.${selectedChord.category}`) || selectedChord.category}
                        </span>
                        <h2 className="font-heading text-5xl sm:text-6xl font-black text-white mt-1 leading-none">
                          {selectedChord.name}
                        </h2>
                        <p className="text-white/80 mt-2">{getChordName(selectedChord)}</p>
                      </div>
                      <button
                        onClick={() => { stopArpeggio(); setSelectedChord(null); }}
                        className="text-white/80 hover:text-white shrink-0"
                        aria-label="Close"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="relative z-10 flex flex-wrap gap-2 mt-4">
                      <span className="text-[10px] uppercase tracking-widest bg-white/15 border border-white/20 text-white px-2.5 py-1 rounded-full font-bold">
                        {t(`levels.${selectedChord.difficulty === 'advanced' ? 'expert' : selectedChord.difficulty}`)}
                      </span>
                      {selectedChord.barre && (
                        <span className="text-[10px] uppercase tracking-widest bg-white/15 border border-white/20 text-white px-2.5 py-1 rounded-full font-bold">
                          {t('chords.barre', { defaultValue: 'Barré' })} {selectedChord.barre}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Body */}
                  <div className="p-6 sm:p-8 space-y-6">
                    {/* Diagram + finger map */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-center">
                      <div className="flex justify-center">
                        <ChordDiagram chord={{...selectedChord, name: selectedChord.name}} size="large" />
                      </div>
                      <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-5">
                        <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-bold mb-2">
                          {t('chords.fretBoardLayout', { defaultValue: 'Corde (6 → 1)' })}
                        </p>
                        <p className="font-mono text-amber text-xl tracking-wider">
                          {selectedChord.positions.map(p => p === -1 ? 'X' : p).join('  ')}
                        </p>
                        <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-bold mt-4 mb-2">
                          {t('chords.fingers', { defaultValue: 'Dita' })}
                        </p>
                        <p className="font-mono text-zinc-300 text-lg tracking-wider">
                          {selectedChord.fingers.map(f => f === 0 ? '–' : f).join('  ')}
                        </p>
                      </div>
                    </div>

                    {/* Theory tidbit */}
                    {theoryNote && (
                      <div className={`rounded-2xl border ${theme.border} ${theme.bg} px-5 py-4 flex items-start gap-3`}>
                        <BookOpen className={`w-4 h-4 mt-0.5 shrink-0 ${theme.text}`} />
                        <div>
                          <p className={`text-[10px] uppercase tracking-widest font-bold mb-1 ${theme.text}`}>
                            {t('chords.theoryNote', { defaultValue: 'Nota di teoria' })}
                          </p>
                          <p className="text-zinc-300 text-sm leading-relaxed">{theoryNote}</p>
                        </div>
                      </div>
                    )}

                    {/* Audio actions */}
                    <div>
                      <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-bold mb-3">
                        {t('chords.listen', { defaultValue: 'Ascolta' })}
                      </p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <Button
                          size="sm"
                          onClick={() => playChordSimultaneous(selectedChord.positions)}
                          className="bg-gradient-to-r from-amber to-orange-500 text-black font-bold"
                          data-testid="chord-play-btn"
                        >
                          <Play className="w-3.5 h-3.5 mr-1" />
                          {t('chords.actionPlay', { defaultValue: 'Suona' })}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => playChordStrum(selectedChord.positions, 'down')}
                          variant="outline"
                          className="border-white/15 text-zinc-200 hover:bg-white/5"
                          data-testid="chord-strum-down-btn"
                        >
                          <ArrowDown className="w-3.5 h-3.5 mr-1" />
                          {t('chords.actionStrumDown', { defaultValue: 'Strum ↓' })}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => playChordStrum(selectedChord.positions, 'up')}
                          variant="outline"
                          className="border-white/15 text-zinc-200 hover:bg-white/5"
                          data-testid="chord-strum-up-btn"
                        >
                          <ArrowUp className="w-3.5 h-3.5 mr-1" />
                          {t('chords.actionStrumUp', { defaultValue: 'Strum ↑' })}
                        </Button>
                        {isPremium ? (
                          <Button
                            size="sm"
                            onClick={() => isArpeggioPlaying ? stopArpeggio() : playArpeggio(selectedChord.positions)}
                            variant="outline"
                            className={`${isArpeggioPlaying
                              ? 'border-red-500/50 text-red-300 hover:bg-red-500/10'
                              : 'border-white/15 text-zinc-200 hover:bg-white/5'}`}
                            data-testid="chord-arpeggio-btn"
                          >
                            <Repeat className="w-3.5 h-3.5 mr-1" />
                            {isArpeggioPlaying ? t('drums.stop') : t('chords.arpeggio')}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            disabled
                            variant="outline"
                            className="border-white/10 text-zinc-500"
                            title={t('chords.proOnly')}
                          >
                            <Lock className="w-3 h-3 mr-1" />
                            {t('chords.arpeggio')}
                          </Button>
                        )}
                      </div>
                    </div>

                    {selectedChord.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-2 border-t border-white/5">
                        {selectedChord.tags.map(tag => (
                          <span key={tag} className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-zinc-500 font-mono">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <Button
                      variant="ghost"
                      className="w-full text-zinc-400"
                      onClick={() => { stopArpeggio(); setSelectedChord(null); }}
                    >
                      {t('common.close', { defaultValue: 'Chiudi' })}
                    </Button>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          );
        })()}

        {/* Empty State */}
        {!loading && chords.length === 0 && (
          <div className="text-center py-20">
            <Music className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">Nessun accordo trovato con i filtri attuali</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChordsPage;
